import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SatisfactionChart from "./SatisfactionChart";
import FeedbackChart from "./FeedbackChart";
import { fetchTicketFeedbackSummary } from "slices/Ticket/thunk";
import { AppDispatch } from "store";
import { Button, Col } from "reactstrap";
import { Link } from "react-router-dom";


const TicketFeedbackSummary: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  const summary = useSelector((state: any) => state.Ticket.ticketFeedbackSummary);
  const loading = useSelector((state: any) => state.Ticket.loading);
  const error = useSelector((state: any) => state.Ticket.error);
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateError, setDateError] = useState("");
  const isNoFeedbackError = error?.success === false && 
    error?.message === "Ticket feedback summary not fetched" && 
    error?.data === null;

  function validateDates(f: string, t: string) {
    if (f && t && f > t) {
      setDateError("From Date must be earlier than To Date.");
    } else {
      setDateError("");
    }
  }

  const handleApply = () => {
    if (!fromDate && toDate) {
      setDateError("Please select From Date.");
      return;
    }

    if (fromDate && !toDate) {
      setDateError("Please select To Date.");
      return;
    }

    if (fromDate && toDate && fromDate > toDate) {
      setDateError("From Date must be earlier than To Date.");
      return;
    }
    setDateError("");
    dispatch(fetchTicketFeedbackSummary({ from_date: fromDate, to_date: toDate }));
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setDateError("");

    // Call API again without filters
    dispatch(fetchTicketFeedbackSummary({ from_date: "", to_date: "" }));
  };

 const satisfactionTotal =
  (summary?.["Satisfaction Breakdown"]?.Good || 0) +
  (summary?.["Satisfaction Breakdown"]?.Average || 0) +
  (summary?.["Satisfaction Breakdown"]?.Bad || 0);

  const isAllZero =
    (summary?.["Feedback Received"] || 0) === 0 &&
    satisfactionTotal === 0 &&
    (summary?.["Auto-Closed (No Feedback)"] || 0) === 0;


  useEffect(() => {
   dispatch(fetchTicketFeedbackSummary({ from_date: "", to_date: "" }));
  }, [dispatch]);

  return (
    <div className="p-4 p-md-5 bg-light min-vh-100">
      <div className="pt-5 rounded-4 bg-light mb-3 ms-3 pe-4">
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="page-title fw-bold dark mb-0 fs-2">
            Feedback & Analytics
          </h2>
          <Button color="primary" outline tag={Link} to="/ticket">
            <i className="ri-stack-line align-middle me-1" />
            View Tickets
          </Button>
        </div>
      </div>
      <div className="filter-section p-4 rounded-3 shadow-sm mb-5 border">
        {dateError && (<p className="text-danger fw-semibold mt-2">{dateError}</p>
        )}
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label fw-semibold text-muted mb-2">From Date</label>
            <input
              type="date"
              max={today}
              className="form-control date-input border-primary-subtle rounded-2 py-2"
              value={fromDate}
              onChange={(e) => {
              setFromDate(e.target.value);
              validateDates(e.target.value, toDate);
            }}
            />
          </div>
          
          <div className="col-md-3">
            <label className="form-label fw-semibold text-muted mb-2">To Date</label>
            <input
              type="date"
              max={today}
              className="form-control date-input border-primary-subtle rounded-2 py-2"
              value={toDate}
              onChange={(e) => {
              setToDate(e.target.value);
              validateDates(fromDate, e.target.value);
            }}

            />
          </div>
          
         <div className="col-md-4 d-flex gap-2">
            <button
              className="btn btn-primary w-80 py-2 fw-semibold apply-btn"
              onClick={handleApply}
            >
              <i className="ri-search-line me-2"></i>Apply Filters
            </button>

            <button
              className="btn btn-primary w-80 py-2 fw-semibold apply-btn"
              type="button"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>
        
        {/* <div className="mt-3 pt-3 border-top">
          <button className="btn btn-outline-secondary view-ticket-btn px-4 py-2">
            <i className="ri-eye-line me-2"></i>View Tickets
          </button>
        </div> */}
      </div>

      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center z-index-9999">
          <div className="text-center text-white p-5">
            <div className="spinner-border text-primary mb-3" style={{width: '3rem', height: '3rem'}}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      )}
        {isNoFeedbackError && !loading && (
          <div className="alert alert-warning d-flex align-items-center shadow-sm mb-5" role="alert">
            <i className="ri-information-line me-3 fs-5"></i>
            <div>
              <h5 className="alert-heading mb-1">No Feedback Available</h5>
              <p className="mb-0">No ticket feedback data found for the selected date range.</p>
            </div>
          </div>
        )}
      {summary && !isNoFeedbackError && !loading && (
        <>
      {!isAllZero && (
        <div className="chart-section row g-4 mb-5 justify-content-center">
          <div className="col-12 col-md-6 col-lg-5 d-flex justify-content-center">
            <SatisfactionChart data={summary?.["Satisfaction Breakdown"] || {}} />
          </div>
          <div className="col-12 col-md-6 col-lg-5 d-flex justify-content-center">
            <FeedbackChart
              received={summary?.["Feedback Received"] || 0}
              autoClosed={summary?.["Auto-Closed (No Feedback)"] || 0}
            />
          </div>
        </div>
      )}
      <div className="summary-table mt-4">
         <div
          className="d-flex align-items-center justify-content-between"
          style={{
            backgroundColor: "#D8F1E8",
            padding: "16px 22px",
            marginBottom: "6px"
          }}
        >
          <div className="fw-semibold text-dark">
            Total Tickets Closed
          </div>

          <div className="fw-semibold text-dark">
            {summary?.["Total Tickets Closed"] ?? 0}
          </div>
        </div>

        {/* Row */}
        <div
          className="d-flex align-items-center"
          style={{ backgroundColor: "#EAF6F1", padding: "14px 18px", marginBottom: "6px"}}>
          <div className="flex-grow-1 fw-semibold text-dark">
            Feedback Received
          </div>
          <div className="fw-semibold text-dark">
            {summary?.["Feedback Received"] || 0}
          </div>
        </div>

        {/* Row */}
        <div
          className="d-flex align-items-center"
          style={{ backgroundColor: "#D8F1E8", padding: "14px 18px", marginBottom : "6px" }}
        >
          <div className="flex-grow-1 fw-semibold text-dark">
            Satisfaction Breakdown
          </div>
          <div className="fw-semibold text-dark">
            {(summary?.["Satisfaction Breakdown"]?.Good || 0) +
              (summary?.["Satisfaction Breakdown"]?.Average || 0) +
              (summary?.["Satisfaction Breakdown"]?.Bad || 0)}
          </div>
        </div>

        {/* Row */}
        <div
          className="d-flex align-items-center"
          style={{ backgroundColor: "#EAF6F1", padding: "14px 18px", marginBottom : "6px" }}
        >
          <div className="flex-grow-1 fw-semibold text-dark">
            Auto-Closed (No Feedback)
          </div>
          <div className="fw-semibold text-dark">
            {summary?.["Auto-Closed (No Feedback)"] || 0}
          </div>
        </div>

        {/* Row */}
        <div
          className="d-flex align-items-center"
          style={{ backgroundColor: "#D8F1E8", padding: "14px 18px", marginBottom : "6px" }} >
          <div className="flex-grow-1 fw-semibold text-dark">
            Average Response Rate
          </div>
          <div className="fw-semibold text-dark">
            {(summary?.["Average Response Rate"] || 0) + "%"}
          </div>
        </div>
      </div>
      
      </>
)}
    </div>
    
  );
};

export default TicketFeedbackSummary;
